import { type ComponentProps, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

export type DiscussionsCommentV2Props = ComponentProps<'article'> & {
  actions?: ReactNode;
  avatar?: ReactNode;
  author: ReactNode;
  comment: ReactNode;
  meta?: ReactNode;
  commentMetadata?: ReactNode;
  commentMetadataOnOwnLine?: boolean;
  secondaryActions?: ReactNode;
  timestamp?: string;
};

export const DiscussionsCommentV2 = memo(
  ({
    actions,
    author,
    avatar,
    className,
    comment,
    commentMetadata,
    commentMetadataOnOwnLine = false,
    meta,
    secondaryActions,
    timestamp,
    ...restOfProps
  }: DiscussionsCommentV2Props) => {
    return (
      <article
        className={cn('group/discussions-comment flex w-full items-start gap-1 px-3 py-4', className)}
        data-testid="DISCUSSIONS_COMMENT"
        {...restOfProps}
      >
        {avatar}
        <div className="flex grow flex-col gap-1">
          <header className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
              <div
                className="max-w-32 truncate font-semibold text-12 text-gray-12"
                data-testid="DISCUSSIONS_COMMENT_AUTHOR"
              >
                {author}
              </div>
              {!commentMetadataOnOwnLine && commentMetadata && (
                <div className="shrink-0 font-semibold text-12 text-gray-12">{commentMetadata}</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!!timestamp && (
                <time
                  className="shrink-0 text-10 text-gray-10"
                  data-testid="DISCUSSIONS_COMMENT_TIMESTAMP"
                  dateTime={timestamp}
                >
                  {timestamp}
                </time>
              )}
              {meta && <div className="mr-2 shrink-0 font-bold text-10 text-gray-10">{meta}</div>}
            </div>
          </header>

          {commentMetadataOnOwnLine && commentMetadata && (
            <div className="font-semibold text-12 text-gray-12">{commentMetadata}</div>
          )}

          <section className="font-medium text-12 text-gray-11">{comment}</section>

          {(secondaryActions || actions) && (
            <div className="flex items-center">
              <div className="group-hover/discussions-comment:[&_*]:!text-gray-12 flex gap-2">{secondaryActions}</div>
              <div className="flex-1" />
              <div className="group-hover/discussions-comment:[&_*]:!text-gray-12 shrink-0 gap-1 text-12 text-gray-10 opacity-0 group-hover/discussions-comment:opacity-100">
                {actions}
              </div>
            </div>
          )}
        </div>
      </article>
    );
  },
);

DiscussionsCommentV2.displayName = 'DiscussionsCommentV2';
